import React, { useCallback, useMemo, useState } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import DirectionsBus from '@mui/icons-material/DirectionsBus';
import { documentType, resolveFileUrl } from './documentUtils';
import ImageLightbox from './ImageLightbox';

const ACCENT = '#2563EB';
const BORDER = '#E5EAF2';

// Images load directly via <img>; no fetch validation, so CORS rules never block previews.
export function busPhotos(bus) {
  const entries = [1, 2, 3, 4].map(index => ({ index, path: bus?.[`upload_photo_${index}`] }))
    .filter(({ path }) => typeof path === 'string' && path.trim() && !/^(null|undefined|false)$/i.test(path.trim()));
  const photos = [];
  let unsupported = 0;
  entries.forEach(({ index, path }) => {
    const url = resolveFileUrl(path);
    if (url && documentType(url) === 'image') photos.push({ index, url });
    else unsupported += 1;
  });
  return { photos, unsupported };
}

function EmptyGallery({ text }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}
      sx={{ bgcolor: '#F4F7FB', border: `1px dashed ${BORDER}`, borderRadius: 2, p: 1.5, minHeight: 72 }}>
      <DirectionsBus color="disabled" fontSize="small" />
      <Typography variant="caption" color="text.secondary">{text}</Typography>
    </Stack>
  );
}

export function MainPreview({ photo, label, onOpen, onError }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <Box component="button" type="button" onClick={onOpen} aria-label={`Open full-size ${label}`}
      sx={{ position: 'relative', display: 'block', width: '100%', height: { xs: 200, md: '100%' }, minHeight: { md: 200 }, p: 0, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#F4F7FB', overflow: 'hidden', cursor: 'zoom-in', '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 } }}>
      {!loaded && <Skeleton variant="rounded" aria-label={`Loading ${label}`} sx={{ position: 'absolute', inset: 0, height: '100%' }} />}
      <Box component="img" src={photo.url} alt={label} onLoad={() => setLoaded(true)} onError={onError}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: loaded ? 1 : 0 }} />
    </Box>
  );
}

// Thumbnails sit under the details on desktop so the preview fills the full card height
// with no blank area beside short field lists; on phones: details, preview, thumbnails.
const layoutStyle = {
  display: 'grid', minWidth: 0, columnGap: 2, rowGap: 1.5,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 9fr) minmax(0, 11fr)' },
  gridTemplateAreas: { xs: '"details" "preview" "thumbs"', md: '"details preview" "thumbs preview"' },
};

export default function BusPhotoGallery({ bus, details = null }) {
  const { photos: allPhotos, unsupported } = useMemo(() => busPhotos(bus), [bus]);
  const [failed, setFailed] = useState(() => new Set());
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const photos = allPhotos.filter(photo => !failed.has(photo.url));
  const selectedIndex = Math.max(0, photos.findIndex(photo => photo.url === selectedUrl));
  const selected = photos[selectedIndex];
  const markFailed = useCallback(url => setFailed(previous => previous.has(url) ? previous : new Set(previous).add(url)), []);
  const step = useCallback(delta => {
    if (photos.length < 2) return;
    setSelectedUrl(photos[(selectedIndex + delta + photos.length) % photos.length].url);
  }, [photos, selectedIndex]);
  const labelFor = photo => `Bus photograph ${photo.index}`;

  if (!selected) {
    const text = allPhotos.length ? 'Bus photographs could not be loaded' : unsupported ? 'Photo attachments are not viewable images' : 'No bus photographs uploaded';
    return <Box sx={{ ...layoutStyle, gridTemplateAreas: { xs: '"details" "preview"', md: '"details preview"' } }}><Box sx={{ gridArea: 'details', minWidth: 0 }}>{details}</Box><Box sx={{ gridArea: 'preview', minWidth: 0 }}><EmptyGallery text={text} /></Box></Box>;
  }

  const hiddenCount = unsupported + failed.size;
  return (
    <Box sx={layoutStyle}>
      <Box sx={{ gridArea: 'details', minWidth: 0 }}>{details}</Box>
      <Box sx={{ gridArea: 'preview', minWidth: 0 }}>
        <MainPreview key={selected.url} photo={selected} label={labelFor(selected)} onOpen={() => setViewerOpen(true)} onError={() => markFailed(selected.url)} />
      </Box>
      <Box sx={{ gridArea: 'thumbs', minWidth: 0, alignSelf: 'end' }}>
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: .5 }} role="group" aria-label="Bus photograph thumbnails">
        {photos.map(photo => {
          const active = photo.url === selected.url;
          return (
            <Box key={photo.url} component="button" type="button" onClick={() => setSelectedUrl(photo.url)} aria-label={`Show ${labelFor(photo)}`} aria-pressed={active}
              sx={{ flex: '0 0 auto', width: { xs: 66, sm: 76 }, aspectRatio: '3 / 4', p: 0, borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer', bgcolor: '#F4F7FB', border: `2px solid ${active ? ACCENT : BORDER}`, boxShadow: active ? `0 0 0 2px ${ACCENT}33` : 'none', transition: 'border-color .15s', '&:hover': { borderColor: active ? ACCENT : '#94A3B8' }, '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 } }}>
              <Box component="img" src={photo.url} alt="" onError={() => markFailed(photo.url)} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" mt={.25} lineHeight={1.4}>
        {photos.length} photo{photos.length === 1 ? '' : 's'} · click the preview to enlarge
        {hiddenCount > 0 && ` · ${hiddenCount} attachment${hiddenCount === 1 ? '' : 's'} unavailable`}
      </Typography>
      </Box>

      <ImageLightbox open={viewerOpen} onClose={() => setViewerOpen(false)} images={photos.map(photo => ({ url: photo.url, label: labelFor(photo) }))}
        index={selectedIndex} onStep={step} onError={markFailed} />
    </Box>
  );
}
